import { useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import {
  Box,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
} from "@material-ui/icons";
import TicketStatusChip from "../../components/ticket-status-chip";
import TicketInfoPanel from "../../components/ticket-info-panel";
import TicketActions from "../../components/ticket-actions";
import CommentThread from "../../components/comment-thread";
import CommentInput from "../../components/comment-input";
import SlaIndicator from "../../components/sla-indicator";
import AssigneeSelector from "../../components/assignee-selector";
import AcknowledgeDialog from "../../components/acknowledge-dialog";
import EscalateDialog from "../../components/escalate-dialog";
import AttachmentPreviewDialog from "../../components/attachment-preview-dialog";

const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg"];
function isImageAttachment(att) {
  const ext = att.file_name?.split(".").pop()?.toLowerCase();
  return (att.file_type && att.file_type.startsWith("image/")) || IMAGE_EXTS.includes(ext);
}

const PRIORITY_CONFIG = {
  critical: { label: "Critical", bgColor: "#FEF2F2", color: "#DC2626" },
  high: { label: "High", bgColor: "#FFF7ED", color: "#EA580C" },
  medium: { label: "Medium", bgColor: "#EFF6FF", color: "#0369A1" },
  low: { label: "Low", bgColor: "#F0FDF4", color: "#16A34A" },
};

const TicketDetailPage = ({
  ticket,
  agents,
  isCommentLoading,
  showAssignDialog,
  showAcknowledgeDialog,
  showEscalateDialog,
  canReply,
  canAcknowledge,
  canAssignAgent,
  canEscalate,
  canChangeStatus,
  canSetPriority,
  onBack,
  onStatusChange,
  onPriorityChange,
  onAssignClick,
  onAssign,
  onAssignDialogClose,
  onAcknowledgeClick,
  onAcknowledge,
  onAcknowledgeDialogClose,
  onEscalateClick,
  onEscalate,
  onEscalateDialogClose,
  onCommentSubmit,
  onRetryComment,
}) => {
  const [previewAttachment, setPreviewAttachment] = useState(null);

  if (!ticket) return null;

  const priority = ticket.priority
    ? PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium
    : null;

  return (
    <>
      <Helmet>
        <title>{ticket.ticket_number} | Kood Asia Management Portal</title>
      </Helmet>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#F8FAFC" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            padding: "12px 24px",
            backgroundColor: "#fff",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              textTransform: "none",
              color: "#64748B",
              fontSize: 14,
              "&:hover": { backgroundColor: "#F8FAFC" },
            }}
          >
            Back to Tickets
          </Button>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 14, color: "#64748B" }}>
              {ticket.ticket_number}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#0F172A" }}>
              {ticket.title}
            </Typography>
          </Box>
          <TicketStatusChip status={ticket.status} />
          {priority ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 10px",
                borderRadius: "12px",
                backgroundColor: priority.bgColor,
                color: priority.color,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: priority.color }} />
              {priority.label}
            </Box>
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 10px",
                borderRadius: "12px",
                backgroundColor: "#F1F5F9",
                color: "#64748B",
                fontSize: 12,
                fontWeight: 500,
                fontStyle: "italic",
              }}
            >
              Pending
            </Box>
          )}
          <TicketActions
            currentStatus={ticket.status}
            currentPriority={ticket.priority}
            canAcknowledge={canAcknowledge}
            canAssignAgent={canAssignAgent}
            canEscalate={canEscalate}
            canChangeStatus={canChangeStatus}
            canSetPriority={canSetPriority}
            onStatusChange={onStatusChange}
            onPriorityChange={onPriorityChange}
            onAssignClick={onAssignClick}
            onAcknowledgeClick={onAcknowledgeClick}
            onEscalateClick={onEscalateClick}
          />
        </Box>

        {/* SLA Bar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 3 },
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "16px 20px",
            margin: "24px 24px 0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <SlaIndicator
            label="Acknowledgement SLA"
            dueDate={ticket.sla_acknowledgement_due}
            respondedAt={ticket.acknowledged_at}
          />
          <Divider orientation="vertical" flexItem />
          <SlaIndicator
            label="First Response SLA"
            dueDate={ticket.sla_response_due}
            respondedAt={ticket.first_response_at}
          />
          <Divider orientation="vertical" flexItem />
          <SlaIndicator
            label="Resolution SLA"
            dueDate={ticket.sla_resolution_due}
            respondedAt={ticket.resolved_at}
          />
        </Box>

        {/* Escalations Info */}
        {ticket.escalations && ticket.escalations.length > 0 && (
          <Box
            sx={{
              backgroundColor: "#FFFBEB",
              borderRadius: "12px",
              padding: "12px 20px",
              margin: "12px 24px 0",
              border: "1px solid #FDE68A",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#92400E", mb: 0.5 }}>
              Escalated Agents
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {ticket.escalations.map((esc) => (
                <Typography key={esc.id} sx={{ fontSize: 13, color: "#92400E" }}>
                  {esc.escalated_to_agent_name}
                  {esc.reason && (
                    <Typography component="span" sx={{ fontSize: 11, color: "#B45309", ml: 0.5 }}>
                      ({esc.reason})
                    </Typography>
                  )}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Main Content: Thread + Info Panel */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, flex: 1, overflow: "hidden", margin: "24px", gap: 3 }}>
          {/* Conversation Thread */}
          <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
            {/* Original Description */}
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                mb: 3,
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#0F172A", mb: 1 }}>
                Description
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: "#0F172A", whiteSpace: "pre-wrap" }}>
                {ticket.description}
              </Typography>
              {(() => {
                const ticketAttachments = (ticket.attachments || []).filter(
                  (att) => !att.comment_id
                );
                if (ticketAttachments.length === 0) return null;
                return (
                  <Box sx={{ display: "flex", gap: "10px", mt: "12px", flexWrap: "wrap" }}>
                    {ticketAttachments.map((att) => {
                      const isImage = isImageAttachment(att);
                      return (
                        <Box
                          key={att.id}
                          onClick={() => setPreviewAttachment(att)}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "8px",
                            border: "1px solid #E2E8F0",
                            overflow: "hidden",
                            cursor: "pointer",
                            transition: "border-color 0.15s, box-shadow 0.15s",
                            "&:hover": { borderColor: "#94A3B8", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                          }}
                        >
                          {isImage && (att.thumbnail_url || att.download_url) ? (
                            <img
                              src={att.thumbnail_url || att.download_url}
                              alt={att.file_name}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#F8FAFC",
                                padding: "6px",
                              }}
                            >
                              <InsertDriveFileOutlinedIcon sx={{ fontSize: 24, color: "#64748B", mb: 0.25 }} />
                              <Typography
                                sx={{
                                  fontSize: 9,
                                  color: "#64748B",
                                  textAlign: "center",
                                  lineHeight: 1.2,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  wordBreak: "break-all",
                                }}
                              >
                                {att.file_name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}
            </Box>

            {/* Comments */}
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                mb: 3,
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#0F172A", mb: 2 }}>
                Conversation
              </Typography>
              <CommentThread comments={ticket.comments} onRetry={onRetryComment} />
            </Box>

            {/* Reply Input — visible only when the user can respond to this ticket */}
            {canReply ? (
              <CommentInput
                onSubmit={onCommentSubmit}
                isLoading={isCommentLoading}
              />
            ) : (
              <Box
                sx={{
                  backgroundColor: "#F8FAFC",
                  border: "1px dashed #CBD5E1",
                  borderRadius: "12px",
                  p: 2,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                  You can only reply to tickets assigned to you.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right Side Info Panel */}
          <TicketInfoPanel ticket={ticket} />
        </Box>
      </Box>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onClose={onAssignDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Ticket</DialogTitle>
        <DialogContent>
          <AssigneeSelector
            agents={agents}
            currentAssignee={ticket.assignee_id}
            onAssign={onAssign}
          />
        </DialogContent>
      </Dialog>

      {/* Acknowledge Dialog */}
      <AcknowledgeDialog
        open={showAcknowledgeDialog}
        agents={agents}
        onClose={onAcknowledgeDialogClose}
        onConfirm={onAcknowledge}
      />

      {/* Escalate Dialog */}
      <EscalateDialog
        open={showEscalateDialog}
        agents={agents}
        currentAssigneeId={ticket.assignee_id}
        escalations={ticket.escalations || []}
        onClose={onEscalateDialogClose}
        onConfirm={onEscalate}
      />

      {/* Attachment Preview Dialog */}
      <AttachmentPreviewDialog
        open={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        fileName={previewAttachment?.file_name}
        fileType={previewAttachment?.file_type}
        downloadUrl={previewAttachment?.download_url}
      />
    </>
  );
};

TicketDetailPage.propTypes = {
  ticket: PropTypes.object,
  agents: PropTypes.array,
  isCommentLoading: PropTypes.bool,
  showAssignDialog: PropTypes.bool,
  showAcknowledgeDialog: PropTypes.bool,
  showEscalateDialog: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onPriorityChange: PropTypes.func.isRequired,
  onAssignClick: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  onAssignDialogClose: PropTypes.func.isRequired,
  onAcknowledgeClick: PropTypes.func.isRequired,
  onAcknowledge: PropTypes.func.isRequired,
  onAcknowledgeDialogClose: PropTypes.func.isRequired,
  onEscalateClick: PropTypes.func.isRequired,
  onEscalate: PropTypes.func.isRequired,
  onEscalateDialogClose: PropTypes.func.isRequired,
  onCommentSubmit: PropTypes.func.isRequired,
};

export default TicketDetailPage;
